/**
 * =============================================================================
 * IDS CRM Web - Reports Page
 * =============================================================================
 *
 * PURPOSE:
 * Surfaces the existing /reports/* API (sales pipeline, support SLA/volume,
 * asset health, cows under management, engineer performance) which previously
 * had no frontend. Manager/admin only - redirects other roles away.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatMonth } from '../utils/format';
import { formatDateKey } from '../utils/dates';
import { formatMoney } from '../utils/money';
import { downloadReportCsv } from '../utils/downloadCsv';
import './Reports.css';

const TABS = [
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'assets', label: 'Assets' },
  { value: 'cows', label: 'Cows' },
  { value: 'engineers', label: 'Engineers' },
  { value: 'financials', label: 'Financials' }
];

const CHART_COLORS = ['#1D9E75', '#534AB7', '#0C447C', '#EF9F27', '#E24B4A'];

function StatCard({ title, value, sub }) {
  return (
    <div className="stat-card">
      <div className="stat-content">
        <h3>{title}</h3>
        <div className="stat-value">{value}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function DataTable({ columns, rows, emptyLabel }) {
  if (!rows || rows.length === 0) {
    return <div className="empty-list">{emptyLabel || 'No data'}</div>;
  }
  return (
    <table className="reports-table">
      <thead>
        <tr>
          {columns.map(col => <th key={col.key}>{col.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx}>
            {columns.map(col => (
              <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DateRangeFilter({ from, to, onChange }) {
  return (
    <div className="reports-date-range">
      <label>
        From
        <input type="date" value={from} onChange={e => onChange({ from: e.target.value, to })} />
      </label>
      <label>
        To
        <input type="date" value={to} onChange={e => onChange({ from, to: e.target.value })} />
      </label>
    </div>
  );
}

function SalesTab({ data, from, to, onDateChange }) {
  if (!data) return <div className="empty-list">Loading...</div>;
  const { summary, by_stage: byStage, by_owner: byOwner, trends } = data;

  return (
    <div>
      <DateRangeFilter from={from} to={to} onChange={onDateChange} />

      <div className="reports-stat-grid">
        <StatCard title="Total Opportunities" value={summary.total} />
        <StatCard title="Total Value" value={formatCurrency(summary.total_value)} />
        <StatCard title="Won" value={summary.won} sub={formatCurrency(summary.won_value)} />
        <StatCard title="Lost" value={summary.lost} />
        <StatCard
          title="Avg Days to Close"
          value={summary.avg_days_to_close ? Math.round(summary.avg_days_to_close) : '-'}
        />
        {/* Orders are not opportunities: kept out of the figures above. */}
        {data.order_book && (
          <StatCard
            title="Orders received"
            value={Number(data.order_book.orders) || 0}
            sub={`${formatCurrency(data.order_book.value)} order book · not in the figures above`}
          />
        )}
      </div>

      {trends && trends.length > 0 && (
        <div className="reports-chart-section">
          <h2>Monthly Trend</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={formatMonth} />
              <YAxis />
              <Tooltip labelFormatter={formatMonth} />
              <Legend />
              <Line type="monotone" dataKey="created" name="Created" stroke={CHART_COLORS[1]} />
              <Line type="monotone" dataKey="won" name="Won" stroke={CHART_COLORS[0]} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="reports-table-section">
        <h2>By Stage</h2>
        <DataTable
          columns={[
            { key: 'stage_name', label: 'Stage' },
            { key: 'count', label: 'Count' },
            { key: 'value', label: 'Value', render: r => formatCurrency(r.value) }
          ]}
          rows={byStage}
        />
      </div>

      <div className="reports-table-section">
        <h2>By Owner</h2>
        <DataTable
          columns={[
            { key: 'full_name', label: 'Owner' },
            { key: 'count', label: 'Opportunities' },
            { key: 'won', label: 'Won' },
            { key: 'value', label: 'Value', render: r => formatCurrency(r.value) }
          ]}
          rows={byOwner}
        />
      </div>
    </div>
  );
}

function SupportTab({ data, from, to, onDateChange }) {
  if (!data) return <div className="empty-list">Loading...</div>;
  const { summary, by_priority: byPriority, by_category: byCategory, by_engineer: byEngineer, sla, trends } = data;

  return (
    <div>
      <DateRangeFilter from={from} to={to} onChange={onDateChange} />

      <div className="reports-stat-grid">
        <StatCard title="Total Tickets" value={summary.total} />
        <StatCard title="Open" value={summary.open} />
        <StatCard title="Closed" value={summary.closed} />
        <StatCard
          title="Avg Response (hrs)"
          value={summary.avg_response_hours ? Number(summary.avg_response_hours).toFixed(1) : '-'}
        />
        <StatCard
          title="Awaiting Response/Resolution"
          value={`${sla.awaiting_response} / ${sla.awaiting_resolution}`}
        />
      </div>

      {trends && trends.length > 0 && (
        <div className="reports-chart-section">
          <h2>Monthly Trend</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={formatMonth} />
              <YAxis />
              <Tooltip labelFormatter={formatMonth} />
              <Legend />
              <Line type="monotone" dataKey="opened" name="Opened" stroke={CHART_COLORS[1]} />
              <Line type="monotone" dataKey="closed" name="Closed" stroke={CHART_COLORS[0]} />
              <Line type="monotone" dataKey="response_breaches" name="SLA Breaches" stroke={CHART_COLORS[4]} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="reports-table-section">
        <h2>By Priority</h2>
        <DataTable
          columns={[
            { key: 'priority', label: 'Priority' },
            { key: 'count', label: 'Count' },
            { key: 'resolved', label: 'Resolved' }
          ]}
          rows={byPriority}
        />
      </div>

      <div className="reports-table-section">
        <h2>By Category</h2>
        <DataTable
          columns={[
            { key: 'category', label: 'Category' },
            { key: 'count', label: 'Count' }
          ]}
          rows={byCategory}
        />
      </div>

      <div className="reports-table-section">
        <h2>By Engineer</h2>
        <DataTable
          columns={[
            { key: 'full_name', label: 'Engineer' },
            { key: 'assigned', label: 'Assigned' },
            { key: 'closed', label: 'Closed' }
          ]}
          rows={byEngineer}
        />
      </div>
    </div>
  );
}

function AssetsTab({ data }) {
  if (!data) return <div className="empty-list">Loading...</div>;
  const { summary, by_health: byHealth, by_type: byType, services_due: servicesDue, recent_services: recentServices } = data;

  return (
    <div>
      <div className="reports-stat-grid">
        <StatCard title="Total Assets" value={summary.total} />
        <StatCard title="Active" value={summary.active} />
        <StatCard title="Overdue Service" value={summary.overdue} />
        <StatCard title="Due in 30 Days" value={summary.due_30d} />
      </div>

      {byHealth && byHealth.length > 0 && (
        <div className="reports-chart-section">
          <h2>By Health Status</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byHealth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="health_flag" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={CHART_COLORS[0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {byType && byType.length > 0 && (
        <div className="reports-chart-section">
          <h2>By Robot Type</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="robot_type" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={CHART_COLORS[1]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="reports-table-section">
        <h2>Services Due</h2>
        <DataTable
          columns={[
            { key: 'asset_ref', label: 'Asset' },
            { key: 'business_name', label: 'Customer' },
            { key: 'task_type', label: 'Task' },
            // A DATE (the task's current_due_date): the day it names
            { key: 'next_service_due', label: 'Due', render: r => formatDateKey(r.next_service_due) }
          ]}
          rows={servicesDue}
        />
      </div>

      <div className="reports-table-section">
        <h2>Recent Services</h2>
        <DataTable
          columns={[
            { key: 'asset_ref', label: 'Asset' },
            { key: 'business_name', label: 'Customer' },
            { key: 'task_type', label: 'Task' },
            // A timestamp: shown on the viewer's own day
            { key: 'completed_at', label: 'Completed', render: r => r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-GB') : '-' }
          ]}
          rows={recentServices}
        />
      </div>
    </div>
  );
}

const GROUP_COLOR = { robots: CHART_COLORS[0], wearables: CHART_COLORS[1], vision: CHART_COLORS[2], other: CHART_COLORS[3] };

function CowsTab({ data }) {
  if (!data) return <div className="empty-list">Loading...</div>;
  const {
    summary,
    by_group: byGroup = [],
    by_line: byLine = [],
    by_territory: byTerritory = [],
    by_country: byCountry = [],
    multi_system_sites: multiSystemSites = [],
    missing_herd_size: missingHerdSize = [],
    unclassified_types: unclassifiedTypes = []
  } = data;

  const num = value => Number(value || 0).toLocaleString('en-GB');

  return (
    <div>
      <div className="reports-stat-grid">
        <StatCard
          title="Cows under Management"
          value={num(summary.cows_under_management)}
          sub={`${num(summary.sites)} sites · ${num(summary.customers)} customers`}
        />
        {byGroup.map(group => (
          <StatCard
            key={group.key}
            title={`Cows under ${group.label}`}
            value={num(group.cows)}
            sub={`${num(group.sites)} sites · ${num(group.assets)} units`}
          />
        ))}
      </div>

      {summary.sites_missing_herd_size > 0 && (
        <div className="reports-note warning">
          <strong>{num(summary.sites_missing_herd_size)} site(s) have no herd size recorded</strong> — their cows are
          missing from every figure here, so all totals are a minimum. The full list is at the bottom of this page.
        </div>
      )}

      {summary.multi_system_sites > 0 && (
        <div className="reports-note">
          <strong>{num(summary.cows_counted_twice)} cows are counted more than once below</strong> across{' '}
          {num(summary.multi_system_sites)} site(s) running more than one system. Group and overall totals count each
          cow once; the product-line rows deliberately overlap, so they add up to more than the total.
        </div>
      )}

      <div className="reports-chart-section">
        <h2>Cows by System Group</h2>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={byGroup}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={value => num(value)} />
            <Bar dataKey="cows" name="Cows" fill={CHART_COLORS[0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="reports-table-section">
        <h2>By System Group</h2>
        <DataTable
          columns={[
            { key: 'label', label: 'Group' },
            { key: 'cows', label: 'Cows (each counted once)', render: r => num(r.cows) },
            { key: 'sites', label: 'Sites', render: r => num(r.sites) },
            { key: 'assets', label: 'Units', render: r => num(r.assets) },
            {
              key: 'overlap',
              label: 'Lines add up to',
              render: r => (r.cows_line_sum > r.cows
                ? <span className="cows-overlap">{num(r.cows_line_sum)} (+{num(r.cows_line_sum - r.cows)} duplicated)</span>
                : num(r.cows_line_sum))
            },
            { key: 'sites_missing_herd_size', label: 'No herd size', render: r => num(r.sites_missing_herd_size) }
          ]}
          rows={byGroup}
          emptyLabel="No installed systems"
        />
      </div>

      <div className="reports-table-section">
        <h2>By System Type</h2>
        <p className="reports-hint">
          A farm running more than one system appears in every row that applies — the duplicated column shows how many
          of that row&apos;s cows are also counted elsewhere.
        </p>
        <DataTable
          columns={[
            {
              key: 'label',
              label: 'System',
              render: r => (
                <span>
                  <span className="cows-group-dot" style={{ background: GROUP_COLOR[r.group] || CHART_COLORS[3] }} />
                  {r.label}
                </span>
              )
            },
            { key: 'group', label: 'Group' },
            { key: 'cows', label: 'Cows', render: r => num(r.cows) },
            {
              key: 'shared_cows',
              label: 'Also counted elsewhere',
              render: r => (r.shared_cows > 0
                ? <span className="cows-overlap">{num(r.shared_cows)} ({num(r.shared_sites)} sites)</span>
                : '-')
            },
            { key: 'sites', label: 'Sites', render: r => num(r.sites) },
            { key: 'assets', label: 'Units', render: r => num(r.assets) },
            { key: 'sites_missing_herd_size', label: 'No herd size', render: r => num(r.sites_missing_herd_size) }
          ]}
          rows={byLine}
          emptyLabel="No installed systems"
        />
      </div>

      <div className="reports-chart-section">
        <h2>Cows by Territory</h2>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={byTerritory}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="territory" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={value => num(value)} />
            <Bar dataKey="cows" name="Cows" fill={CHART_COLORS[2]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="reports-table-section">
        <h2>By Territory</h2>
        <DataTable
          columns={[
            { key: 'territory', label: 'Territory' },
            { key: 'cows', label: 'Cows', render: r => num(r.cows) },
            { key: 'customers', label: 'Customers', render: r => num(r.customers) },
            { key: 'sites', label: 'Sites', render: r => num(r.sites) },
            { key: 'assets', label: 'Units', render: r => num(r.assets) },
            { key: 'sites_missing_herd_size', label: 'No herd size', render: r => num(r.sites_missing_herd_size) }
          ]}
          rows={byTerritory}
          emptyLabel="No installed systems"
        />
      </div>

      <div className="reports-table-section">
        <h2>By Country</h2>
        <DataTable
          columns={[
            { key: 'country_code', label: 'Country' },
            { key: 'territory', label: 'Territory' },
            { key: 'cows', label: 'Cows', render: r => num(r.cows) },
            { key: 'sites', label: 'Sites', render: r => num(r.sites) },
            { key: 'assets', label: 'Units', render: r => num(r.assets) },
            { key: 'sites_missing_herd_size', label: 'No herd size', render: r => num(r.sites_missing_herd_size) }
          ]}
          rows={byCountry}
          emptyLabel="No installed systems"
        />
      </div>

      <div className="reports-table-section">
        <h2>Sites with More Than One System</h2>
        <p className="reports-hint">These herds are counted once in the totals, but appear under each system they run.</p>
        <DataTable
          columns={[
            { key: 'site_name', label: 'Site' },
            { key: 'business_name', label: 'Customer' },
            { key: 'cows', label: 'Cows', render: r => (r.has_herd_size ? num(r.cows) : <span className="cows-missing">Not recorded</span>) },
            { key: 'systems', label: 'Systems', render: r => (r.systems || []).join(', ') }
          ]}
          rows={multiSystemSites}
          emptyLabel="No site runs more than one system"
        />
      </div>

      <div className="reports-table-section">
        <h2>Sites Missing a Herd Size</h2>
        <p className="reports-hint">
          Filling in Cows Milking on these farm sites is all that stands between these figures and the real number.
        </p>
        <DataTable
          columns={[
            { key: 'site_name', label: 'Site' },
            { key: 'business_name', label: 'Customer' },
            { key: 'country_code', label: 'Country', render: r => r.country_code || '-' },
            { key: 'assets', label: 'Units', render: r => num(r.assets) },
            { key: 'systems', label: 'Systems', render: r => (r.systems || []).join(', ') || '-' }
          ]}
          rows={missingHerdSize}
          emptyLabel="Every site with an installed system has a herd size"
        />
      </div>

      {unclassifiedTypes.length > 0 && (
        <div className="reports-note warning">
          <strong>Unclassified product types:</strong> {unclassifiedTypes.join(', ')} — these count towards the overall
          total and the &quot;Other&quot; group, but have no system row. Add them to the report grouping to break them out.
        </div>
      )}
    </div>
  );
}

function EngineersTab({ data, from, to, onDateChange }) {
  if (!data) return <div className="empty-list">Loading...</div>;
  const rows = data.data || [];

  return (
    <div>
      <DateRangeFilter from={from} to={to} onChange={onDateChange} />

      {rows.length > 0 && (
        <div className="reports-chart-section">
          <h2>Tasks Completed / Tickets Closed</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="full_name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="tasks_completed" name="Tasks Completed" fill={CHART_COLORS[0]} />
              <Bar dataKey="tickets_closed" name="Tickets Closed" fill={CHART_COLORS[2]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="reports-table-section">
        <h2>Engineer Performance</h2>
        <DataTable
          columns={[
            { key: 'full_name', label: 'Engineer' },
            { key: 'primary_region', label: 'Region' },
            { key: 'current_status', label: 'Status' },
            { key: 'tasks_completed', label: 'Tasks Completed' },
            { key: 'tickets_closed', label: 'Tickets Closed' }
          ]}
          rows={rows}
        />
      </div>
    </div>
  );
}

/**
 * Batch C: Financials tab - period cost/charge rollup on the consolidated
 * time data (work-log rate snapshots), with the billable/warranty split.
 */
function FinancialsTab({ data, from, to, onDateChange }) {
  if (!data) return <div className="empty-list">Loading...</div>;
  const { summary, by_customer: byCustomer, by_engineer: byEngineer } = data;
  const money = (minor) => formatMoney(minor);

  return (
    <div>
      <DateRangeFilter from={from} to={to} onChange={onDateChange} />

      <div className="reports-stat-grid">
        <StatCard title="Labour Hours" value={summary.hours} sub={`${summary.work_logs} work logs`} />
        <StatCard title="Internal Cost" value={money(summary.total_internal_cost_minor_units)} sub="labour + parts" />
        <StatCard title="Billable" value={money(summary.total_billable_minor_units)} sub={`${summary.billable_tickets} billable tickets`} />
        <StatCard
          title="Cover Absorbed"
          value={money(summary.total_cover_absorbed_minor_units ?? summary.total_warranty_absorbed_minor_units)}
          sub={`${summary.covered_tickets ?? summary.warranty_tickets} covered tickets (warranty + contract)`}
        />
        <StatCard title="Parts used" value={summary.parts_fitted} sub={money(summary.parts_cost_minor_units) + ' cost'} />
      </div>

      <div className="reports-table-section">
        <h2>By Customer</h2>
        <DataTable
          columns={[
            { key: 'business_name', label: 'Customer' },
            { key: 'tickets', label: 'Tickets' },
            { key: 'hours', label: 'Hours' },
            { key: 'labour_cost', label: 'Internal Cost', render: (row) => money(row.labour_cost) },
            { key: 'billable_charge', label: 'Billable', render: (row) => money(row.billable_charge) },
            { key: 'covered_absorbed', label: 'Cover Absorbed', render: (row) => money(row.covered_absorbed ?? row.warranty_absorbed) }
          ]}
          rows={byCustomer}
          emptyLabel="No ticket work in this period"
        />
      </div>

      <div className="reports-table-section">
        <h2>By Engineer</h2>
        <DataTable
          columns={[
            { key: 'full_name', label: 'Engineer' },
            { key: 'work_logs', label: 'Work Logs' },
            { key: 'hours', label: 'Hours' },
            { key: 'labour_cost', label: 'Cost', render: (row) => money(row.labour_cost) },
            { key: 'labour_charge', label: 'Charge Value', render: (row) => money(row.labour_charge) }
          ]}
          rows={byEngineer}
          emptyLabel="No completed work logs in this period"
        />
      </div>
    </div>
  );
}

const EXPORT_TYPE_BY_TAB = { sales: 'opportunities', support: 'tickets', assets: 'assets' };

function Reports() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = TABS.some(t => t.value === searchParams.get('tab')) ? searchParams.get('tab') : 'sales';
  const [from, setFrom] = useState(searchParams.get('from') || '');
  const [to, setTo] = useState(searchParams.get('to') || '');
  const [tabData, setTabData] = useState({});
  const [exporting, setExporting] = useState(false);

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  useEffect(() => {
    if (user && !isManager) {
      navigate('/');
    }
  }, [user, isManager, navigate]);

  const fetchTab = useCallback(async (tab, currentFrom, currentTo) => {
    const params = new URLSearchParams();
    if (currentFrom) params.append('from', currentFrom);
    if (currentTo) params.append('to', currentTo);
    const response = await authFetch(`/api/v1/reports/${tab}?${params}`);
    if (!response.ok) return;
    const data = await response.json();
    setTabData(prev => ({ ...prev, [tab]: data }));
  }, [authFetch]);

  useEffect(() => {
    if (!isManager) return;
    fetchTab(activeTab, from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, from, to, isManager]);

  const handleTabChange = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const handleDateChange = ({ from: newFrom, to: newTo }) => {
    setFrom(newFrom);
    setTo(newTo);
  };

  const handleExport = async () => {
    const exportType = EXPORT_TYPE_BY_TAB[activeTab];
    if (!exportType) return;
    setExporting(true);
    try {
      await downloadReportCsv(authFetch, exportType, from, to);
    } catch (error) {
      console.error('CSV export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!isManager) return null;

  return (
    <div className="reports-page">
      <div className="dashboard-header">
        <div>
          <h1>Reports</h1>
          <p>Sales pipeline, support, asset, and engineer performance</p>
        </div>
        {EXPORT_TYPE_BY_TAB[activeTab] && (
          <div className="header-actions">
            <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        )}
      </div>

      <div className="reports-tabs">
        {TABS.map(tab => (
          <button
            key={tab.value}
            className={`reports-tab ${activeTab === tab.value ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'sales' && (
        <SalesTab data={tabData.sales} from={from} to={to} onDateChange={handleDateChange} />
      )}
      {activeTab === 'support' && (
        <SupportTab data={tabData.support} from={from} to={to} onDateChange={handleDateChange} />
      )}
      {activeTab === 'assets' && (
        <AssetsTab data={tabData.assets} />
      )}
      {activeTab === 'cows' && (
        <CowsTab data={tabData.cows} />
      )}
      {activeTab === 'engineers' && (
        <EngineersTab data={tabData.engineers} from={from} to={to} onDateChange={handleDateChange} />
      )}
      {activeTab === 'financials' && (
        <FinancialsTab data={tabData.financials} from={from} to={to} onDateChange={handleDateChange} />
      )}
    </div>
  );
}

export default Reports;
